import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ComicCard from "@/components/feature/ComicCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import SearchBar from "@/pages/search/components/SearchBar";
import { COMICS, REGIONS } from "@/mocks/comics";
import { filterComics, paginate, searchComics, relatedComics } from "@/lib/comics";
import { usePageMeta, useScrollTop } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;
const HOT_KEYWORDS = ["机甲", "悬疑", "恋爱", "热血", "国漫", "治愈", "奇幻", "完结"];

export default function SearchPage() {
  const [params] = useSearchParams();
  const keyword = (params.get("q") || "").trim();
  const [region, setRegion] = useState("全部");
  const [page, setPage] = useState(1);

  const matched = useMemo(() => searchComics(keyword), [keyword]);
  const result = useMemo(() => filterComics(matched, { region }), [matched, region]);
  const paged = useMemo(() => paginate(result, page, PAGE_SIZE), [result, page]);

  const fallback = useMemo(() => (keyword ? relatedComics(COMICS[0], 4) : []), [keyword]);

  useScrollTop(`${keyword}-${page}`);
  usePageMeta(
    keyword ? `「${keyword}」搜索结果 - 禁漫天堂` : "漫画搜索 - 禁漫天堂",
    keyword
      ? `禁漫天堂搜索「${keyword}」相关漫画资源，共找到 ${matched.length} 部作品，包含热血、恋爱、奇幻、悬疑等多类别漫画，可查看详情与章节资源。`
      : "使用禁漫天堂搜索功能，通过漫画名称、作者或题材关键词快速查找动漫漫画资源。",
    keyword ? `禁漫天堂,${keyword},漫画搜索,漫画资源` : "禁漫天堂,漫画搜索,动漫下载",
  );

  const changeRegion = (value: string) => {
    setRegion(value);
    setPage(1);
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
        <nav aria-label="面包屑导航" className="flex items-center gap-1.5 text-xs text-foreground-400">
          <Link to="/" className="hover:text-accent-300 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-600" />
          <span className="text-foreground-200">搜索结果</span>
        </nav>

        <div className="mt-4 rounded-lg border hairline bg-background-900/60 p-5 md:p-6">
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
            {keyword ? <>「{keyword}」搜索结果</> : "搜索漫画资源"}
          </h1>
          <p className="mt-1.5 text-xs md:text-sm text-foreground-400">
            {keyword ? `共找到 ${matched.length} 部与「${keyword}」相关的作品，可继续修改关键词或按地区筛选。` : "输入关键词开始查找，支持漫画名称、作者、题材标签与简介全文检索。"}
          </p>
          <div className="mt-4 max-w-[640px]">
            <SearchBar key={keyword} initialKeyword={keyword} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-500">试试</span>
            {HOT_KEYWORDS.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                onClick={() => setPage(1)}
                className={`h-7 px-2.5 rounded-full text-[11px] border transition-colors whitespace-nowrap ${
                  tag === keyword
                    ? "bg-accent-500/20 border-accent-500/50 text-accent-300"
                    : "bg-background-950/50 border-background-700/70 text-foreground-300 hover:text-accent-300 hover:border-accent-500/40"
                }`}
              >
                {tag}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-500 mr-1">地区</span>
          {["全部", ...REGIONS].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => changeRegion(item)}
              className={`h-8 px-3 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                region === item
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-900/70 border hairline text-foreground-300 hover:border-primary-400/50"
              }`}
            >
              {item}
            </button>
          ))}
          <span className="ml-auto text-xs text-foreground-500">
            第 {paged.page} / {paged.totalPages} 页
          </span>
        </div>

        <div className="mt-4">
          {!keyword ? (
            <EmptyState
              title="输入关键词开始搜索"
              description="例如输入「机甲」「悬疑」「恋爱」，或直接使用上方的热门关键词。"
              suggestions={[
                { label: "浏览分类", to: "/category/fantasy" },
                { label: "返回首页", to: "/" },
              ]}
            />
          ) : result.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
              {paged.items.map((comic, i) => (
                <div key={comic.id} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                  <ComicCard comic={comic} query={keyword} />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title={`没有找到与「${keyword}」相关的漫画`}
              description={`我们暂时没有匹配「${keyword}」的作品。可以换一个更短的关键词，或按地区筛选后重新查看。`}
              onReset={() => changeRegion("全部")}
              resetLabel="重置地区筛选"
              suggestions={[
                { label: "热血漫画", to: "/category/hot-blood" },
                { label: "悬疑漫画", to: "/category/mystery" },
              ]}
            />
          )}
        </div>

        {keyword && result.length > 0 && (
          <Pagination page={paged.page} totalPages={paged.totalPages} onPage={setPage} />
        )}

        {keyword && (
          <section aria-labelledby="search-suggest" className="mt-8">
            <h2 id="search-suggest" className="font-heading text-base md:text-lg font-semibold text-foreground-50 mb-3">
              在看这些的人也在找
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {fallback.map((item) => (
                <ComicCard key={item.id} comic={item} />
              ))}
            </div>
          </section>
        )}
      </div>
      <div className="h-12" />
    </SiteLayout>
  );
}